import { Heading } from "@cmp-src/data-display/Heading/Heading";
import { UserAvatar } from "@cmp-src/data-display/UserAvatar/UserAvatar";
import { useIsMobile } from "@cmp-src/hooks/use-mobile";
import { FavoriteIcon } from "@cmp-src/icons/icons";
import { Badge } from "@cmp-src/primitives/badge";
import { Button } from "@cmp-src/primitives/button";
import { useAskTessa } from "@cmp-src/tessa/AskTessaProvider";
import { ISchedule, IUserFull } from "@gettactic/api";
import { cn, formatStartEndSlotHours } from "@gettactic/helpers";
import { Star } from "lucide-react";
import React from "react";

export interface UserLabelProps {
  user: Partial<IUserFull>;
  className?: string;
  allowFavorite?: boolean;
  allowTessa?: boolean;
  showTitle?: boolean;
  showEmail?: boolean;
  showTeams?: boolean;
  teams?: { id: string; name: string }[];
  variant?:
    | "default"
    | "dropdown"
    | "gantt"
    | "directory"
    | "directory-lead"
    | "host"
    | "sidebar";
  onMouseEnter?: (event: React.MouseEvent<HTMLDivElement>) => void;
  onMouseLeave?: (event: React.MouseEvent<HTMLDivElement>) => void;
  onClick?: (event: React.MouseEvent<HTMLDivElement>) => void;
  showAvatar?: boolean;
  schedules?: ISchedule[];
  /** Replaces the schedule time line (e.g. "Dedicated desk"). */
  statusText?: React.ReactNode;
  allowTruncate?: boolean;
  isOrgPrivacyEnabled?: boolean;
  currentUserId?: string;
  rightOfName?: React.ReactNode;
}

const classes = {
  Wrapper: "group flex items-center",
  InfoWrapper: "flex w-full flex-1 flex-col items-start text-left",
  UserInfo: "text-muted-foreground",
  FavoriteWrapper:
    "self-start opacity-0 transition-opacity group-hover:opacity-100",
  FavoriteWrapperMobile: "self-start opacity-100 transition-opacity",
  UserName: "flex items-center justify-between"
};

const variantClasses = {
  default: {
    Wrapper: "h-[86px] gap-4",
    AvatarWrapper: "h-[70px] w-[70px]",
    Avatar: "h-[70px] w-[70px]",
    InfoWrapper: "gap-1.5",
    UserName: "",
    UserInfo: "text-[12px]"
  },
  dropdown: {
    Wrapper: "h-[47px] gap-2.5",
    AvatarWrapper: "h-[28px] w-[28px]",
    Avatar: "h-[28px] w-[28px]",
    InfoWrapper: "gap-0",
    UserName: "font-[400] text-[13px]",
    UserInfo: "text-2xs"
  },
  gantt: {
    Wrapper: "gap-2.5",
    AvatarWrapper: "h-[26px] w-[26px]",
    Avatar: "h-[26px] w-[26px]",
    InfoWrapper: "gap-0",
    UserName: "font-[400] text-[13px]",
    UserInfo: "text-2xs"
  },
  directory: {
    Wrapper: "gap-2.5 px-6 overflow-hidden min-w-0",
    AvatarWrapper: "h-[36px] w-[36px] flex-shrink-0",
    Avatar: "h-[36px] w-[36px]",
    InfoWrapper: "gap-0 min-w-0",
    UserName: "font-[400] text-[13px]",
    UserInfo: "text-2xs"
  },
  "directory-lead": {
    Wrapper: "h-[86px] gap-4 min-w-0 overflow-hidden max-w-full",
    AvatarWrapper: "h-[70px] w-[70px] flex-shrink-0",
    Avatar: "h-[70px] w-[70px]",
    InfoWrapper: "gap-1.5 min-w-0 overflow-hidden max-w-full",
    UserName: "text-xl font-bold truncate",
    UserInfo: "text-[12px] uppercase font-semiBold"
  },
  host: {
    Wrapper: "gap-3 min-w-0 overflow-hidden",
    AvatarWrapper: "h-[36px] w-[36px] flex-shrink-0",
    Avatar: "h-[36px] w-[36px]",
    InfoWrapper: "gap-0.5 min-w-0",
    UserName: "font-medium text-[17px] text-neutral-900",
    UserInfo: "text-[13px] text-neutral-500"
  },
  sidebar: {
    Wrapper: "gap-2.5 min-w-0 overflow-hidden",
    AvatarWrapper: "h-[28px] w-[28px] flex-shrink-0",
    Avatar: "h-[28px] w-[28px]",
    InfoWrapper: "gap-0 min-w-0",
    UserName: "font-semibold text-[14px] text-neutral-1000",
    UserInfo: "text-2xs"
  }
};

export const UserLabel: React.FC<UserLabelProps> = ({
  user,
  className,
  allowFavorite = false,
  allowTessa = false,
  showTitle = true,
  showEmail = false,
  showTeams = false,
  teams,
  variant = "default",
  onClick,
  onMouseEnter,
  onMouseLeave,
  showAvatar = true,
  schedules,
  statusText,
  allowTruncate = false,
  isOrgPrivacyEnabled = false,
  currentUserId,
  rightOfName
}) => {
  const selectedClasses = variantClasses[variant || "default"];
  const tessa = useAskTessa();
  const isMobile = useIsMobile();

  // Hide user information when privacy is enabled and it's not the current user
  const shouldHideUserInfo = isOrgPrivacyEnabled && user.id !== currentUserId;

  return (
    <div
      className={cn(
        classes.Wrapper,
        selectedClasses.Wrapper,
        className,
        allowTruncate && "min-w-0 overflow-hidden"
      )}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
    >
      {showAvatar && (
        <div className={selectedClasses.AvatarWrapper}>
          {shouldHideUserInfo ? (
            <UserAvatar
              user={{
                name: "Private",
                id: "N/A",
                email: "N/A",
                profile_photo_url: null,
                roles: ["deleted"]
              }}
              size="full"
              className={cn(selectedClasses.Avatar)}
            />
          ) : (
            <UserAvatar
              user={user}
              size="full"
              className={selectedClasses.Avatar}
            />
          )}
        </div>
      )}
      <div
        className={cn(
          "flex flex-1 items-center",
          allowTruncate && "min-w-0 overflow-hidden"
        )}
      >
        <div
          className={cn(
            classes.InfoWrapper,
            selectedClasses.InfoWrapper,
            allowTruncate && "min-w-0 overflow-hidden"
          )}
        >
          {rightOfName ? (
            <div className="flex min-w-0 max-w-full items-center gap-2">
              <Heading
                as={"h5"}
                className={cn(
                  !allowTruncate && classes.UserName,
                  selectedClasses.UserName,
                  allowTruncate && "min-w-0 truncate",
                  "font-inter"
                )}
              >
                {shouldHideUserInfo
                  ? "Private"
                  : user.name +
                    (user.roles?.includes("deleted") ? " (inactive)" : "")}
              </Heading>
              <span className="flex shrink-0 items-center">{rightOfName}</span>
            </div>
          ) : (
            <Heading
              as={"h5"}
              className={cn(
                !allowTruncate && classes.UserName,
                selectedClasses.UserName,
                allowTruncate && "w-full max-w-full truncate",
                "font-inter"
              )}
            >
              {shouldHideUserInfo
                ? "Private"
                : user.name +
                  (user.roles?.includes("deleted") ? " (inactive)" : "")}
            </Heading>
          )}
          {showTitle && (
            <p
              className={
                "font-inter " +
                cn(
                  classes.UserInfo,
                  selectedClasses.UserInfo,
                  allowTruncate && "truncate w-full max-w-full",
                  "font-semiBold"
                )
              }
            >
              {shouldHideUserInfo ? "Private" : user.title}
            </p>
          )}
          {showEmail && !shouldHideUserInfo && (
            <p
              className={cn(
                classes.UserInfo,
                selectedClasses.UserInfo,
                allowTruncate && "truncate w-full max-w-full"
              )}
            >
              {user.email}
            </p>
          )}
          {statusText && (
            <p
              className={cn(
                classes.UserInfo,
                selectedClasses.UserInfo,
                "text-[13px] text-black",
                "flex items-center gap-2",
                "font-inter"
              )}
            >
              {statusText}
            </p>
          )}
          {!statusText && schedules && schedules.length > 0 && (
            <p
              className={cn(
                classes.UserInfo,
                selectedClasses.UserInfo,
                "text-[13px] text-black",
                "flex items-center gap-2",
                "font-inter"
              )}
            >
              {formatStartEndSlotHours(schedules[0], " → ")}
              {schedules.length > 1 && (
                <Badge
                  variant={"outline"}
                  className={
                    "!border !border-neutral-100 px-1.5 py-0.5 font-inter font-normal text-[#7E7E7E] text-[12px]"
                  }
                >
                  {schedules.length - 1} more
                </Badge>
              )}
            </p>
          )}
          {showTeams && teams && teams.length > 0 && !shouldHideUserInfo && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {teams.map((team) => (
                <Badge
                  key={team.id}
                  variant="outline"
                  className="text-xs font-normal text-muted-foreground"
                >
                  {team.name}
                </Badge>
              ))}
            </div>
          )}
        </div>
        {/* RESTORE IT LATER WHEN RELESE TESSA */}
        {/*{tessa && allowTessa && (
          <div
            className={cn(
              isMobile ? classes.FavoriteWrapperMobile : classes.FavoriteWrapper
            )}
          >
            <Button
              variant={"icon"}
              onClick={(event) => {
                event.stopPropagation();
                event.preventDefault();

                tessa?.open({
                  targets: [
                    {
                      id: user.id || "",
                      type: "user",
                      label: user.name || "User"
                    }
                  ]
                });
              }}
            >
              <AiIcon className={"size-3.5"} />
            </Button>
          </div>
        )}*/}
        {/*{allowFavorite && (
          <div
            className={cn(
              isMobile ? classes.FavoriteWrapperMobile : classes.FavoriteWrapper
            )}
          >
            <Button variant={"icon"}>
              <Star className={"size-3.5"} />
            </Button>
          </div>
        )}*/}
      </div>
    </div>
  );
};
